import { Head, Link } from "@inertiajs/react"
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react"
import { buildVideoEmbedUrl } from "@/lib/video-embed"
import { CoachLayout } from "../components/CoachLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"

export default function TutorialVideosShow({ currentUser, video, allVideos }) {
  const currentIndex = allVideos.findIndex((v) => v.id === video.id)
  const prevVideo = currentIndex > 0 ? allVideos[currentIndex - 1] : null
  const nextVideo =
    currentIndex < allVideos.length - 1 ? allVideos[currentIndex + 1] : null

  // Build the player URL through the shared helper (KTD4/KTD7) — the same
  // recognizer the member and admin-preview surfaces use, so a plain Vimeo
  // share link plays here too (it silently failed before). A null result
  // (unrecognized link) renders no iframe instead of an un-framable page.
  const embedUrl = buildVideoEmbedUrl(video.vimeoUrl)

  return (
    <CoachLayout currentUser={currentUser}>
      <Head title={`${video.title} - Tutorial Videos - Kingdom Factor`} />

      <div className="space-y-6">
        <Link
          href="/app/coach/tutorial_videos"
          className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Tutorials
        </Link>

        <div>
          <h1 className="font-display text-2xl font-bold text-zinc-900">
            {video.title}
          </h1>
        </div>

        {/* Vimeo Embed */}
        {embedUrl && (
          <Card className="overflow-hidden">
            <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
              <iframe
                src={embedUrl}
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                title={video.title}
              />
            </div>
          </Card>
        )}

        {/* Description */}
        {video.description && (
          <Card>
            <CardContent className="py-5">
              <p className="text-zinc-700 leading-relaxed whitespace-pre-line">
                {video.description}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Previous / Next Navigation */}
        {(prevVideo || nextVideo) && (
          <>
            <Separator />
            <div className="flex items-center justify-between gap-4">
              {prevVideo ? (
                <Link
                  href={`/app/coach/tutorial_videos/${prevVideo.slug}`}
                  className="group flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900 transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                  <span>
                    <span className="text-xs text-zinc-400 block">
                      Previous
                    </span>
                    {prevVideo.title}
                  </span>
                </Link>
              ) : (
                <div />
              )}
              {nextVideo ? (
                <Link
                  href={`/app/coach/tutorial_videos/${nextVideo.slug}`}
                  className="group flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900 transition-colors text-right"
                >
                  <span>
                    <span className="text-xs text-zinc-400 block">Next</span>
                    {nextVideo.title}
                  </span>
                  <ChevronRight className="h-4 w-4" />
                </Link>
              ) : (
                <div />
              )}
            </div>
          </>
        )}
      </div>
    </CoachLayout>
  )
}
